/**
 * @file BulkBillSummary.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-12
 * @modified 2026-10-12
 * @description Bulk Add Bills (2026-10-12): the summary — one row per bill
 *              found in the batch: where it came from (file, pages), the
 *              supplier, invoice number, date, net / VAT / total as printed,
 *              its PO, its status (Ready / Needs review with the reasons /
 *              Possible duplicate / Unreadable / Saved / Skipped…) and what an
 *              Accept would do ("marks PO-… billed · updates 2 PO costs ·
 *              goes to Xero as Approved"). Every row carries the same actions,
 *              greyed with the reason where they do not apply: preview the
 *              pages, Accept (Ready rows only), Review (queue it), decide a
 *              duplicate, Skip / Discard, Undo, open the saved bill.
 */

import React from 'react';
import TickCheckbox from './TickCheckbox';
import EntityLink from './EntityLink';
import { formatPrice } from '../utils/currencyFormatter';
import { formatDate } from '../utils/dateFormatters';
import { PLAN_STATUS, sourceText } from '../utils/bulkBillPlan';
import './TableStyles.css';
import './BulkAddBills.css';

const TONE = {
  ready: 'ok', review: 'warn', duplicate: 'warn', unreadable: 'error', not_a_bill: 'warn', failed: 'error',
  reading: 'muted', checking: 'muted', saved: 'ok', replaced: 'ok', kept_existing: 'muted', skipped: 'muted', discarded: 'muted',
};
const UNDOABLE = ['kept_existing', 'skipped', 'discarded'];
const money = (v) => (v === null || v === undefined || v === '' ? '—' : formatPrice(v));

function ActionButton({ icon, label, onClick, disabled, reason, tone = '' }) {
  return (
    <button
      type="button"
      className={`btn-icon ${tone}${disabled ? ' btn-disabled' : ''}`}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={disabled ? `${label} — ${reason}` : label}
      aria-label={label}
      data-write-action={['visibility', 'open_in_new'].includes(icon) ? undefined : true}
    >
      <span className="material-symbols-rounded">{icon}</span>
    </button>
  );
}

function BulkBillSummary({
  items, plans, selected, onToggle, onToggleAll, busyId = null, busy = false,
  onPreview, onAccept, onReview, onDuplicate, onSkip, onDiscard, onReopen, onAttach, onRetryRead, onOpenBill,
}) {
  const rows = items || [];
  const selectable = rows.filter(i => !i.outcome);
  const allTicked = selectable.length > 0 && selectable.every(i => selected.has(i.id));

  return (
    <div className="table-container bulk-bills-table-wrap">
      <table className="data-table bulk-bills-table">
        <thead>
          <tr>
            <th className="checkbox-col">
              <TickCheckbox id="bulk-bills-all" checked={allTicked} onChange={() => onToggleAll(!allTicked)}
                ariaLabel="Select every bill still to do" disabled={!selectable.length} />
            </th>
            <th>#</th>
            <th>From</th>
            <th>Supplier</th>
            <th>Invoice no.</th>
            <th>Date</th>
            <th className="num">Net</th>
            <th className="num">VAT</th>
            <th className="num">Total</th>
            <th>PO</th>
            <th>Status</th>
            <th>Accept would</th>
            <th className="actions-col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(item => {
            const plan = plans.get(item.id) || { status: 'checking', reasons: [], notes: [], effects: [] };
            const s = item.parsed?.suggested || {};
            const settled = !!item.outcome;
            const reading = plan.status === 'reading' || plan.status === 'checking';
            const dup = (item.duplicates?.length || 0) > 0 || !!item.batch_duplicate_of;
            const isBill = item.kind === 'bill' || item.kind === 'credit';
            const thisBusy = busyId === item.id;
            const why = settled ? 'already settled' : reading ? 'still being read' : null;
            return (
              <tr key={item.id} className={`bulk-row status-${plan.status}${item.choice === 'review' && !settled ? ' queued' : ''}`}>
                <td className="checkbox-col">
                  <TickCheckbox id={`bulk-bill-${item.id}`} checked={selected.has(item.id)} disabled={settled}
                    onChange={() => onToggle(item.id)} ariaLabel={`Select bill ${item.seq}`} />
                </td>
                <td>{item.seq}</td>
                <td className="bulk-src" title={sourceText(item)}>
                  <span className="material-symbols-rounded">picture_as_pdf</span>
                  <span>{sourceText(item)}</span>
                </td>
                <td>{plan.supplier?.name || s.supplier_name || (isBill ? '—' : '')}{item.kind === 'credit' && <span className="bulk-tag">Credit</span>}</td>
                <td>{s.supplier_reference || '—'}</td>
                <td>{s.bill_date ? formatDate(s.bill_date) : '—'}</td>
                <td className="num">{money(s.sub_total ?? plan.totals?.subTotal)}</td>
                <td className="num">{money(s.total_tax ?? plan.totals?.totalTax)}</td>
                <td className="num">{money(s.total ?? plan.totals?.total)}</td>
                <td>
                  {(plan.poNumbers || []).join(', ') || (s.matched_po?.purchase_order_number) || (s.po_reference ? <span className="muted" title="Quoted on the invoice, not found here">{s.po_reference}?</span> : '—')}
                </td>
                <td className="bulk-status">
                  <span className={`bulk-badge ${TONE[plan.status] || 'muted'}`}>
                    {thisBusy ? 'Saving…' : PLAN_STATUS[plan.status] || plan.status}
                  </span>
                  {item.choice === 'review' && !settled && <span className="bulk-tag">To review</span>}
                  {item.dup_choice === 'continue' && !settled && <span className="bulk-tag">Save as new (flagged)</span>}
                  {item.bill && (
                    <div className="bulk-saved-as">
                      <EntityLink type="bill" id={item.bill.id}>{item.bill.bill_number || item.bill.supplier_reference || `Bill #${item.bill.id}`}</EntityLink>
                    </div>
                  )}
                  {dup && !settled && (
                    <div className="bulk-dup-line">
                      {item.duplicates?.length > 0
                        ? <>Looks like {item.duplicates.map((d, i) => (
                          <React.Fragment key={d.id}>{i ? ', ' : ''}<EntityLink type="bill" id={d.id}>{d.bill_number || d.supplier_reference || `bill #${d.id}`}</EntityLink></React.Fragment>
                        ))}</>
                        : <>Same invoice as row {rows.find(r => r.id === item.batch_duplicate_of)?.seq ?? '?'} of this batch</>}
                    </div>
                  )}
                  {!settled && plan.reasons?.length > 0 && plan.status !== 'duplicate' && (
                    <ul className="bulk-reasons">{plan.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
                  )}
                  {!settled && plan.notes?.length > 0 && (
                    <ul className="bulk-notes">{plan.notes.map((r, i) => <li key={i}>{r}</li>)}</ul>
                  )}
                  {item.attach_error && (
                    <div className="bulk-reasons">
                      {item.attach_error}{' '}
                      <button type="button" className="bulk-link" onClick={() => onAttach(item)} data-write-action>Attach again</button>
                    </div>
                  )}
                  {item.read_state === 'failed' && !settled && isBill && (
                    <button type="button" className="bulk-link" onClick={() => onRetryRead(item)} data-write-action>Read it again</button>
                  )}
                </td>
                <td className="bulk-effects">
                  {plan.status === 'ready' ? plan.effects.join(' · ') : <span className="muted">—</span>}
                </td>
                <td className="actions-col">
                  <div className="actions-cell">
                    <ActionButton icon="visibility" label="Preview its pages" onClick={() => onPreview(item)}
                      disabled={!item.id} reason="no pages" />
                    <ActionButton icon="check_circle" label="Accept — save it as normal" tone="accept"
                      onClick={() => onAccept([item.id])}
                      disabled={busy || plan.status !== 'ready'}
                      reason={why || (plan.status === 'duplicate' ? 'decide the possible duplicate first'
                        : 'it needs review (see why under its status)')} />
                    <ActionButton icon="rate_review" label={item.choice === 'review' ? 'Take off the review list' : 'Review — work through it in Add Bill'}
                      onClick={() => onReview([item.id], item.choice !== 'review')}
                      disabled={busy || settled || reading} reason={why} />
                    <ActionButton icon="content_copy" label="Possible duplicate — keep existing, replace or continue"
                      onClick={() => onDuplicate(item)}
                      disabled={busy || settled || !dup} reason={why || 'nothing in the system looks like it'} />
                    {isBill ? (
                      <ActionButton icon="skip_next" label="Skip — do not save it" onClick={() => onSkip(item)}
                        disabled={busy || settled || reading} reason={why} />
                    ) : (
                      <ActionButton icon="delete" label="Discard these pages" onClick={() => onDiscard(item)}
                        disabled={busy || settled} reason={why} />
                    )}
                    <ActionButton icon="undo" label="Undo — bring it back to do" onClick={() => onReopen(item)}
                      disabled={busy || !UNDOABLE.includes(item.outcome)}
                      reason={item.outcome === 'saved' || item.outcome === 'replaced' ? 'it was saved — change it from the bill' : 'nothing to undo'} />
                    <ActionButton icon="open_in_new" label="Open the saved bill" onClick={() => onOpenBill(item.bill_id)}
                      disabled={!item.bill_id} reason="not saved" />
                  </div>
                </td>
              </tr>
            );
          })}
          {!rows.length && (
            <tr><td colSpan={13} className="muted bulk-empty">No bills yet — the files are being split into bills.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default BulkBillSummary;
